import { ComponentType, Key, useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Item } from '@react-stately/collections'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { TikTokBrand, YouTubeBrand } from 'blaze-ui/icons/20'
import { Text } from 'blaze-ui/Text/Text'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ScheduledPostTarget } from 'types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { useScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useScheduledPostTarget'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { DesignChannel } from 'components/NewFileModal/Types'
import { TikTokPostSettings } from 'components/PostPreviewModal/components/TikTokPostSettings'
import { YoutubePostDetails } from 'components/PostPreviewModal/components/Youtube/YoutubePostDetails'
import { ConnectAccountEmptyState } from './ConnectAccountEmptyState'
import Styles from './AdjustPrivacySettingsModal.module.scss'

interface Props {
  scheduledPost: ScheduledPostShow
}

export type SupportedProvider = ExternalIntegrationEnum.TikTok | ExternalIntegrationEnum.YouTube

const SUPPORTED_PROVIDERS: SupportedProvider[] = [ExternalIntegrationEnum.TikTok, ExternalIntegrationEnum.YouTube]

interface FormProps {
  scheduledPost: ScheduledPostShow
  scheduledPostTarget?: ScheduledPostTarget
  channel: DesignChannel
  isModal?: boolean
  compact?: boolean
}

const PROVIDER_CHANNEL: Record<SupportedProvider, DesignChannel> = {
  [ExternalIntegrationEnum.TikTok]: DesignChannel.TikTokPost,
  [ExternalIntegrationEnum.YouTube]: DesignChannel.YoutubeShort,
}

const PROVIDER_ICON: Record<SupportedProvider, typeof TikTokBrand> = {
  [ExternalIntegrationEnum.TikTok]: TikTokBrand,
  [ExternalIntegrationEnum.YouTube]: YouTubeBrand,
}

const PROVIDER_LABEL: Record<SupportedProvider, string> = {
  [ExternalIntegrationEnum.TikTok]: 'TikTok',
  [ExternalIntegrationEnum.YouTube]: 'YouTube',
}

const PROVIDER_FORM: Record<SupportedProvider, ComponentType<FormProps>> = {
  [ExternalIntegrationEnum.TikTok]: TikTokPostSettings,
  [ExternalIntegrationEnum.YouTube]: YoutubePostDetails,
}

const FormSkeleton = () => (
  <div className={Styles.skeleton}>
    {[0, 1, 2].map((i) => (
      <div key={i} className={Styles.skeletonSection}>
        <LoadingShimmerer className={Styles.skeletonHeader} borderRadius={4} />
        <LoadingShimmerer className={Styles.skeletonInput} borderRadius={8} />
      </div>
    ))}
  </div>
)

interface ProviderFormProps {
  provider: SupportedProvider
  scheduledPost: ScheduledPostShow
  targetId: number
}

const ProviderForm = ({ provider, scheduledPost, targetId }: ProviderFormProps) => {
  const { data: target } = useScheduledPostTarget(scheduledPost.id, targetId)
  if (!target) return <FormSkeleton />
  const Form = PROVIDER_FORM[provider]
  return (
    <Form
      scheduledPost={scheduledPost}
      scheduledPostTarget={target}
      channel={PROVIDER_CHANNEL[provider]}
      isModal
      compact
    />
  )
}

export const AdjustPrivacySettingsModal = ({ scheduledPost: initialPost }: Props) => {
  const { closeModal } = useModals()
  const { data: integrationsData } = useExternalIntegrations()
  const { data: scheduledPost } = useScheduledPost({ id: initialPost.id })

  const [active, setActive] = useState<SupportedProvider>(SUPPORTED_PROVIDERS[0])

  const renderForm = (provider: SupportedProvider) => {
    const connected = integrationsData?.external_integrations?.some((i) => i.provider === provider) ?? false
    if (!connected) {
      return <ConnectAccountEmptyState provider={provider} />
    }

    if (!scheduledPost) return <FormSkeleton />
    const target = scheduledPost.scheduled_post_targets.find((t) => t.provider === provider)
    if (!target) {
      const Form = PROVIDER_FORM[provider]
      return (
        <div className={Styles.disabled} aria-disabled="true">
          <Form
            scheduledPost={scheduledPost}
            scheduledPostTarget={undefined}
            channel={PROVIDER_CHANNEL[provider]}
            isModal
            compact
          />
        </div>
      )
    }
    return <ProviderForm provider={provider} scheduledPost={scheduledPost} targetId={target.id} />
  }

  return (
    <Modal.Root bodyClassName={Styles.modal}>
      <Modal.Header title="Adjust privacy settings" />
      <Modal.Content className={Styles.body}>
        <SegmentedControl
          activeItem={active}
          onItemClick={(key: Key) => setActive(key as SupportedProvider)}
          isFullWidth
        >
          {SUPPORTED_PROVIDERS.map((provider) => {
            const Icon = PROVIDER_ICON[provider]
            return (
              <Item key={provider} textValue={PROVIDER_LABEL[provider]}>
                <span className={Styles.tabLabel}>
                  <Icon />
                  <Text variant="primary">{PROVIDER_LABEL[provider]}</Text>
                </span>
              </Item>
            )
          })}
        </SegmentedControl>
        {renderForm(active)}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton onPress={closeModal}>Save</Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
